import { type OutstandingDetailsHeaderOwnProps } from "components/sectionHeader/detailsHeader/types";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { campaignsStateSelector } from "../state/selectors";
import { bindAction } from "interfaces/redux";
import { connect, type ConnectedProps } from "react-redux";
import { useMemo, type FC } from "react";
import { useNavigate } from "react-router";
import { DetailsHeader } from "components/sectionHeader";
import PublishValidationInfo from "components/publishValidationInfo/PublishValidationInfo";
import { lockCampaign } from "../state/thunks/campaignEntityStateThunk";
import { CreateCampaignSendButton } from "../Details/Sends/CreateCampaignSendButton";
import SelectedItemsLabel from "components/sectionHeader/selectedItemsLabel/SelectedItemsLabel";

interface EditCampaignDetailsHeaderContainerPropsBase extends OutstandingDetailsHeaderOwnProps {
  campaignId: number;
  campaignsOverviewUrl: string;
  back: () => void;
  onErrorClick: () => void;
  isSendsTab: boolean;
  isSendDisabled: boolean;
}

type EditCampaignDetailsHeaderProps = EditCampaignDetailsHeaderContainerPropsBase & PropsFromRedux;

const EditCampaignDetailsHeader: FC<EditCampaignDetailsHeaderProps> = (props: EditCampaignDetailsHeaderProps) => {
  const navigate = useNavigate();
  const {
    title,
    back,
    campaignId,
    campaignsOverviewUrl,
    lockCampaign,
    campaignValidation: { errorMode, emailTemplateValidation, landingPageValidation, feedbackPageValidation },
    onErrorClick,
    isSendsTab,
    selectedSendIds,
    isSendDisabled,
  } = props;

  const onClose = () => navigate(campaignsOverviewUrl);
  const onEdit = () => lockCampaign(campaignId);

  const hasActiveErrors =
    !emailTemplateValidation.resolved || !landingPageValidation.resolved || !feedbackPageValidation.resolved;

  const renderCustomHeader = () => {
    return (
      errorMode && (
        <PublishValidationInfo
          unableToPublish={hasActiveErrors}
          readyToPublish={!hasActiveErrors}
          onErrorButtonClick={onErrorClick}
        />
      )
    );
  };

  const headerTitle = useMemo(() => {
    const selectedSendsCount = selectedSendIds.length;
    if (selectedSendsCount && isSendsTab) {
      return <SelectedItemsLabel itemName={"Send"} selectedItemsAmount={selectedSendsCount} shouldPluralize />;
    } else {
      return title;
    }
  }, [title, selectedSendIds.length, isSendsTab]);

  return (
    <DetailsHeader
      title={headerTitle}
      pageTitle={title}
      titleForGA="Phishing Campaign Details"
      backButton={back || campaignsOverviewUrl}
      defaultURL={campaignsOverviewUrl}
      canBeEdited={props.canBeEdited}
      crudPermission={props.crudPermission}
      canBePublished={props.canBePublished}
      isRevertVisible={props.isRevertVisible}
      invalidFormDetails={props.invalidFormDetails}
      publishedStatus={props.publishedStatus}
      entityStateActions={{
        onClose,
        onPublish: props.entityStateActions.onPublish,
        onEdit,
        onRevert: props.entityStateActions.onRevert,
      }}
      hideEntityStateButtons={props.hideEntityStateButtons}
    >
      {props.hideEntityStateButtons || renderCustomHeader()}
      {isSendsTab && !selectedSendIds.length && <CreateCampaignSendButton id={campaignId} disabled={isSendDisabled} />}
    </DetailsHeader>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;
  return {
    campaignValidation: base.campaignValidation,
    selectedSendIds: base.sends.overview.selected,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  lockCampaign: bindAction(lockCampaign, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditCampaignDetailsHeader);
export default ConnectedComponent;
